import {
	addNewRelicPageAction,
	setNewRelicCustomAttribute
} from '@ddc/new-relic';
import {
	filterNonInventoryParams,
	findSBParamsMappingKey,
	getQueryStringObj as getQueryStringObjUtil,
	getQueryString as getQueryStringUtil
} from 'wsm-wis-client';
import { hasOwn } from '../utilities/utils';
import { getSortOptions } from '../utilities/sortOptions';
import { processWISResponse } from '../utilities/processWISResponse';
import {
	updateDdcDataLayerFromWIS,
	setDataLayerTracking
} from '../utilities/tracking';

/**
 * InvDataRefactored - Modern WIS-only implementation
 *
 * This class is a refactored version of InvData that removes all legacy GVM databus
 * integration while preserving the required public API for backward compatibility.
 *
 * Key changes from legacy InvData:
 * - Removed all GVM databus calls and dual-path logic
 * - Simplified constructor (no useWIS parameter)
 * - Removed _requestResource() method entirely
 * - Uses only modern WIS endpoints
 * - Public API trimmed as part of US1778499 (legacy, unused getters removed)
 * - Method privacy enforced as part of TA3663769 (internal-only methods now private)
 *
 * Deprecated/Removed Public Getters (US1778499):
 * - getSpellcheck()                (internal spellcheck value retained for workflow only)
 * - getUseOriginalSearchTerm()     (flag retained internally; no external consumption)
 * - getForceOriginalSearch()       (flag retained internally; no external consumption)
 *
 * Made Private (TA3663769):
 * - _clearForceOriginalSearch()
 * - _clearOriginalSearchTerm()
 * - _clearSpellcheckMsg()
 * - _setLocalStorageForOriginalSearch()
 * - _setLocalStorageForForceSearch()
 * - _updateUrlParams()
 *
 * Internal flags and properties remain for existing search correction logic but are
 * no longer exposed as getters. Documentation updated in `docs/refactoring/public-api-contract.md`.
 *
 * Related Epics / Stories:
 * - US1665547: Remove GVM databus from ws-inv-data
 * - US1778499: Remove unused legacy public methods
 *   - TA3663767: Remove unused public getters
 *   - TA3663769: Enforce method privacy for internal-only methods
 *   - TA3663771: Extract query string utilities to url-params-refactored.js
 */
export class InvDataRefactored {
	constructor(
		requestSpellcheck,
		requestWISInventoryAndFacets,
		prefs,
		labels,
		parameterMap = {},
		ssrData = null,
		serverGeoZipSource = 'none',
		serverResolvedGeoZip = null
	) {
		this.prefs = prefs;
		this.labels = labels;
		this.parameterMap = parameterMap;
		this.ssrData = ssrData;

		// Initial state
		this.start = 0;
		this.urlParams = {};

		// Data returned from WIS services
		this.facets = {};
		this.appliedFilters = [];
		// Inventory is populated either by server-side Redux hydration (ssrData)
		// or by the client-side fetch in _loadInitialData. Starts empty: the old
		// preload location (window.DDC.InvData.inventory) was removed with
		// inline/preload*.js, so there is nothing to read here on construction.
		this.inventory = {};
		this.spellcheck = '';
		this.appliedSpellcheck = false;
		this.originalSearchTerm = '';
		this.srpReady = false;
		this.sortOptions = getSortOptions(prefs, labels);
		this.sortBy = '';
		this.filterType = '';
		this.filterSearchEventData = {};

		// Event topics - must remain unchanged for widget compatibility
		this.TOPIC_PREFIX = 'ws-inv-data';
		this.FACETS_TOPIC = `${this.TOPIC_PREFIX}/facets`;
		this.INVENTORY_TOPIC = `${this.TOPIC_PREFIX}/inventory`;
		this.SPELLCHECK_TOPIC = `${this.TOPIC_PREFIX}/spellcheck`;
		this.SORT_BY_TOPIC = `${this.TOPIC_PREFIX}/sortBy`;
		this.TOGGLE_LISTING_VIEW_TOPIC = `${this.TOPIC_PREFIX}/toggleListingView`;

		// Legacy properties removed:
		// - this.WINDOW_ID = 'inventory-data-bus1' (GVM-specific)
		// - this.FACETS_API_URL, this.INVENTORY_API_URL, etc. (GVM endpoints)
		// - this.isWIS (always true in refactored version)
		// - this.fetchers (hysteriFetch for GVM endpoints)
		// - this.SEARCH_API_URL

		this.DEBOUNCE_WAIT = 100;

		this.useOriginalSearchTerm = false;
		this.forceOriginalSearch = false;

		// Geolocation handling
		if (this.prefs.geoLocationEnabled === 'true') {
			// Add New Relic tracking attribute to monitor geoLocationEnabled usage
			setNewRelicCustomAttribute('srpGeoLocationEnabled', 'true');
			// Matches the server-side attribute name set in store.js so NRQL
			// queries can correlate without having to alias by event type.
			setNewRelicCustomAttribute(
				'serverGeoZipSource',
				serverGeoZipSource
			);

			// Seed geoZip from the zip the SSR fetch actually used
			// (serverResolvedGeoZip), not client storage. This keeps the UI
			// honest: the facet/URL reflect the exact zip that bounded the
			// rendered inventory, rather than a possibly-stale localStorage value
			// that could differ from what the server resolved from URL/cookie/
			// edgescape. Null when SSR resolved no geo (serverGeoZipSource
			// 'none') — we leave geoZip null rather than pretend a zip applied.
			// The geoData object is always defined when geo is enabled so
			// setFilterSelections('geoZip', ...) and other mutation paths have a
			// stable shape to write into.
			// See DDC-WebPlatform/docs: teams/blue-flannel/adr/srp-geolocation/
			// adr-0001-server-side-zip-resolution.md.
			const geoZip = serverResolvedGeoZip ? [serverResolvedGeoZip] : null;

			this.geoData = {
				geoZip,
				geoRadius: [this.prefs.defaultGeoDistRadius]
			};
			this.geoDataPrefs = {
				geoRadiusValues: this.prefs.geoRadiusValues.replaceAll(' ', ''),
				defaultGeoDistRadius: this.prefs.defaultGeoDistRadius
			};
		}

		// Track listing config ID
		if (prefs['listing.config.id']) {
			setNewRelicCustomAttribute(
				'listingConfigId',
				prefs['listing.config.id']
			);
		}

		// Store WIS request functions passed from Widget.jsx
		this.requestSpellcheck = requestSpellcheck;
		this.requestWISInventoryAndFacets = requestWISInventoryAndFacets;

		// Simplified spellcheck request - WIS only
		this._requestSpellcheck = async () => {
			this.requestSpellcheck(this.urlParams.search.toString()).then(
				(correction) => {
					this.spellcheck = correction;
					this._setLocalStorageForOriginalSearch();
					this._requestInventoryAndFacets('spellcheck');
				}
			);
		};

		// last-attempted request (success or failure), for
		// retryLastRequest(). urlParams/start/sortBy are read at retry time, not
		// snapshotted, since they aren't rolled back on failure and may have moved on.
		this._lastRequest = null;

		// Unified request for both inventory and facets - US1778481
		this._requestInventoryAndFacets = window._.debounce(
			(eventType, eventData = {}) => {
				this._lastRequest = { eventType, eventData };

				if (this.spellcheck && !this.forceOriginalSearch) {
					this.urlParams.search = [this.spellcheck];
				} else if (this.forceOriginalSearch) {
					this.urlParams.search = [this.originalSearchTerm];
				}

				// Standard unified request
				// eslint-disable-next-line no-unused-vars
				const {
					start: _start,
					sortBy: _sortBy,
					...urlParams
				} = this.urlParams;
				this.requestWISInventoryAndFacets(
					urlParams,
					this.start,
					this.sortBy,
					eventType,
					eventData
				);
			},
			this.DEBOUNCE_WAIT
		);

		this._publishSortBy = window._.debounce(() => {
			this.publishEvent(this.SORT_BY_TOPIC, this.sortBy);
		}, this.DEBOUNCE_WAIT);

		// Expose instance before _loadInitialData so that PubSub subscribers
		// (e.g. ws-inv-listing reacting to 'ws-inv-data/inventory') can
		// reference window.DDC.InvData during the synchronous publish calls
		// inside the constructor. Without this, the assignment in Widget.jsx
		// hasn't executed yet and subscribers see undefined.
		if (typeof window !== 'undefined' && window.DDC) {
			window.DDC.InvData = this;
		}

		this._loadInitialData();
	}

	// =====================================
	// PUBLIC API METHODS - MUST BE PRESERVED
	// =====================================

	// Event Registration Methods (5 methods)
	registerFacetNotification(callback) {
		this.subscribeEvent(this.FACETS_TOPIC, callback);
	}

	registerInventoryNotification(callback) {
		this.subscribeEvent(this.INVENTORY_TOPIC, callback);
	}

	registerSortByNotification(callback) {
		this.subscribeEvent(this.SORT_BY_TOPIC, callback);
	}

	// TODO: Remove this method when functionality is removed from ddc-js-api
	registerToggleListingView(callback) {
		this.subscribeEvent(this.TOGGLE_LISTING_VIEW_TOPIC, callback);
	}

	// Data Getter Methods (11 methods)
	getFacets() {
		return this.facets;
	}

	getSortOptions() {
		return this.sortOptions;
	}

	getInventory() {
		return this.inventory;
	}

	getPaging() {
		return this.inventory?.pageInfo || null;
	}

	getSortBy() {
		return this.sortBy;
	}

	getAppliedFilters() {
		return this.appliedFilters;
	}

	getGeoData() {
		return this.geoData;
	}

	getGeoDataPrefs() {
		return this.geoDataPrefs;
	}

	// Legacy Support Method - Always returns true in refactored version
	/* eslint-disable class-methods-use-this */
	getIsWIS() {
		// In the refactored version, we're always using WIS
		return true;
	}
	/* eslint-enable class-methods-use-this */

	// State Mutation Methods (7 methods)
	// Applies a complete urlParams snapshot as-is. Two callers:
	//   1. clearFilters() — passes a snapshot it has already cleared (and which
	//      owns the geoRadius='0' "show all inventory" decision).
	//   2. the onpopstate (back/forward) handler in Widget.jsx — passes the
	//      snapshot captured in history.state.
	// Do NOT mutate geo here. Back/forward must faithfully restore whatever
	// geoRadius the snapshot recorded; clobbering it would reset the user's
	// chosen radius on every navigation.
	replaceSelections(urlParams, sortBy, start, updateBrowserHistory = true) {
		this.urlParams = urlParams;
		this.start = start;
		this.sortBy = sortBy;

		if (updateBrowserHistory) {
			this._updateBrowserHistory();
		}

		this._requestInventoryAndFacets();
	}

	clearFilters() {
		const urlParams = filterNonInventoryParams(
			this.urlParams,
			this.appliedFilters
		);

		// "Clear all" intentionally drops the distance bound: geoZip survives as
		// a non-inventory param (keeping the user's location for centering and
		// distance sort), but geoRadius='0' removes the radius restriction so
		// results span all inventory rather than the previously selected radius.
		if (urlParams.geoZip) {
			urlParams.geoRadius = ['0'];
		}

		this._clearSpellcheckMsg();
		this._clearForceOriginalSearch();
		this._clearOriginalSearchTerm();
		this.replaceSelections(urlParams, this.sortBy, 0 /* start */, true);
	}

	// re-issues the last attempted WIS request (same eventType/
	// eventData) with current urlParams/start/sortBy, so widgets can offer a
	// retry after a failed call. No-op if nothing has been requested yet.
	retryLastRequest() {
		if (!this._lastRequest) {
			return;
		}

		const { eventType, eventData } = this._lastRequest;
		this._requestInventoryAndFacets(eventType, eventData);
	}

	setSortBy(value = '') {
		this.sortBy = value.split();

		this._updateBrowserHistory();
		this._requestInventoryAndFacets('sort');
		this._publishSortBy();
	}

	setOriginalSearchTerm() {
		this.useOriginalSearchTerm = true;
		this.spellcheck = '';
		if (this.originalSearchTerm) {
			setNewRelicCustomAttribute(
				'selectedOriginalSearchTerm',
				this.originalSearchTerm
			);
		}
		this._setLocalStorageForForceSearch();
		this._clearSpellcheckMsg();
		this._requestInventoryAndFacets();
	}

	spellcheckSearchTerm(event, facetInfo) {
		window.DDC.InvData.clearFilters();
		if (facetInfo.search) {
			const searchValue = [];
			searchValue.push(facetInfo.search);
			this.urlParams.search = searchValue;
			window.history.pushState(
				{
					urlParams: this.urlParams
				},
				'',
				`${window.location.origin}${
					window.location.pathname
				}${this.getQueryString()}`
			);
		}
		this._requestSpellcheck();

		// Persisting geoZip and geoRadius
		const geoData = this.getAppliedFilters()
			.filter((key) => key.id.includes('geo'))
			.reduce((obj, item) => {
				return {
					...obj,
					[item.id]: item.values[0].value
				};
			}, {});

		const newFacetInfo = {
			...geoData,
			...facetInfo
		};

		Object.keys(newFacetInfo).forEach((name) => {
			window.DDC.InvData.setFilterSelections({
				filterName: name,
				filterValues: [newFacetInfo[name]],
				filterValue: newFacetInfo[name],
				element: event.target
			});
		});
	}

	/**
	 *
	 * @param {string} filterName Name of filter (ex: 'make')
	 * @param {string[]} filterValues Values of filter (ex: ['BMW', 'Toyota'])
	 * @param {string} filterValue most recently selected value of the filter, used for tracking
	 * @param {string} filterTitleText Optional Title label as the user sees it (ex: 'Fuel Type')
	 * @param {boolean} [checked=true] if filter is being added (true) or removed (false)
	 * @param {boolean} [isSlider=false] if filter is slider
	 * @param {boolean} [isBreadcrumb=false] if filter is breadcrumb
	 * @param element {object} element interacted with
	 */
	setFilterSelections({
		filterName,
		filterValues,
		filterValue,
		element,
		isSelected = true,
		isSlider = false,
		isChip = false,
		filterTitleText = ''
	}) {
		this._updateUrlParams({ filterName, filterValues });
		this.start = 0;
		this.filterType = filterName;
		this.filterSearchEventData = {
			clickedFacetName: filterName,
			clickedFacetValue: filterValue,
			clickedFacetTitleText: filterTitleText,
			checked: isSelected,
			isSlider,
			isBreadcrumb: isChip,
			element
		};

		if (filterName === 'geoZip') {
			// The location facet only sets geoZip — it never sends geoRadius on
			// this path. When the page rendered without a server-resolved geo,
			// urlParams has no geoRadius (it's only merged in _loadInitialData
			// when a zip was already present), so the WIS request would go out
			// unbounded — results not geo-bounded and no radius surfaced in the
			// URL / applied filters. Seed the default radius from geoData ONLY
			// when no radius is applied yet — don't clobber a radius the shopper
			// already chose (e.g. picked 200mi, then changed the zip). On clear
			// (empty input) drop both.
			const zip = filterValues?.[0];
			if (zip) {
				this.geoData.geoZip = filterValues;
				if (!this.urlParams.geoRadius) {
					this.urlParams.geoRadius = this.geoData.geoRadius;
				}
			} else {
				this.geoData.geoZip = null;
				delete this.urlParams.geoRadius;
			}
		}

		this._updateBrowserHistory();

		// clear the spellcheck so that it will allow to clear filters
		if (!Object.keys(this.urlParams).includes('search')) {
			this._clearSpellcheckMsg();
			this._clearForceOriginalSearch();
			this._clearOriginalSearchTerm();
		}
		const eventType = filterName === 'search' ? 'search' : 'facet';
		this._requestInventoryAndFacets(eventType, this.filterSearchEventData);
	}

	setStart(index, updateBrowserHistory = true) {
		this.start = index.toString().split();

		if (updateBrowserHistory) {
			this._updateBrowserHistory();
		}

		this._requestInventoryAndFacets();
	}

	// Utility Methods (2 methods)
	// Delegates to the shared wsm-wis-client query-string utilities
	getQueryStringObj(overrides = {}) {
		return getQueryStringObjUtil(
			this.urlParams,
			this.sortBy,
			this.start,
			overrides
		);
	}

	getQueryString(overrides = {}) {
		return getQueryStringUtil(
			this.urlParams,
			this.sortBy,
			this.start,
			overrides
		);
	}

	// =====================================
	// PRIVATE METHODS
	// =====================================

	_clearForceOriginalSearch() {
		if (this.forceOriginalSearch) {
			try {
				window.localStorage.removeItem('forceOriginalSearch');
			} catch {
				// localStorage inaccessible (Safari private mode / cross-origin iframe)
			}
			this.forceOriginalSearch = false;
		}
	}

	_clearOriginalSearchTerm() {
		if (this.originalSearchTerm) {
			try {
				window.localStorage.removeItem('originalSearchTerm');
			} catch {
				// localStorage inaccessible (Safari private mode / cross-origin iframe)
			}
			this.originalSearchTerm = '';
		}
	}

	_clearSpellcheckMsg() {
		this.spellcheck = '';
		this.useOriginalSearchTerm = false;
		this.publishEvent(`${this.TOPIC_PREFIX}/clearSpellcheckMsg`);
	}

	_setLocalStorageForOriginalSearch() {
		if (Object.keys(this.urlParams).includes('search')) {
			try {
				window.localStorage.setItem(
					'originalSearchTerm',
					this.urlParams.search.toString()
				);
			} catch {
				// localStorage inaccessible (Safari private mode / cross-origin iframe)
			}
			setNewRelicCustomAttribute(
				'originalSearchTerm',
				this.originalSearchTerm
			);
			setNewRelicCustomAttribute('CorrectedSearchTerm', this.spellcheck);
			this.useOriginalSearchTerm = true;
		}
	}

	_setLocalStorageForForceSearch() {
		try {
			window.localStorage.setItem('forceOriginalSearch', 'true');
			this.forceOriginalSearch = window.localStorage.getItem(
				'forceOriginalSearch'
			);
		} catch {
			// localStorage inaccessible (Safari private mode / cross-origin iframe)
			this.forceOriginalSearch = 'true';
		}
	}

	_updateUrlParams({ filterName, filterValues }) {
		// On redirect from a missing VDP, the corresponding make and model are automatically applied as filters
		// based on the the vdpUrl param. Here, we replace the vdpUrl param with params for the filters.
		if (hasOwn(this.urlParams, 'vdpUrl')) {
			this.appliedFilters.forEach(({ id, values }) => {
				if (!hasOwn(this.urlParams, id)) {
					this.urlParams[id] = [values[0]?.value];
				}
			});
			delete this.urlParams.vdpUrl;
		}

		// find a SB param key if it exists to add or remove facet
		const SBParamKey = findSBParamsMappingKey(filterName);
		const hasLegacyParam =
			SBParamKey.length > 0 &&
			Object.prototype.hasOwnProperty.call(this.urlParams, SBParamKey);

		if (!filterValues || !filterValues.length) {
			// Handle removing deprecated legacy `incentiveId` param due to conditional applied filter logic for `incentiveIds`
			if (
				filterName === 'incentiveIds' &&
				!this.urlParams.incentiveIds &&
				this.urlParams.incentiveId
			) {
				delete this.urlParams.incentiveId;
			} else {
				// Handle removing SB param from the url
				if (hasLegacyParam) {
					delete this.urlParams[SBParamKey];
				}
				delete this.urlParams[filterName];
			}
		} else {
			// Handle removing only SB param from the url with other facet choices selected
			if (
				hasLegacyParam &&
				!filterValues.includes(this.urlParams[SBParamKey][0])
			) {
				delete this.urlParams[SBParamKey];
			}
			this.urlParams[filterName] = filterValues;
		}
	}

	_updateBrowserHistory() {
		window.history.pushState(
			{
				urlParams: this.urlParams,
				sortBy: this.sortBy,
				start: this.start
			},
			'',
			`${window.location.origin}${
				window.location.pathname
			}${this.getQueryString()}`
		);
	}

	/* eslint-disable class-methods-use-this */
	publishEvent(topic, ...params) {
		if (window && window.DDC && window.DDC.pubsub) {
			window.DDC.pubsub.publish(topic, ...params);
		} else if (typeof window !== 'undefined') {
			// pubsub not loaded yet — this publish is lost. Track it so we can
			// see whether pubsub-not-ready is a real production concern before
			// investing in a buffering mechanism. See issue #344.
			setNewRelicCustomAttribute('pubsubNotReadyOnPublish', topic);
		}
	}

	subscribeEvent(topic, callback) {
		if (window && window.DDC && window.DDC.pubsub) {
			window.DDC.pubsub.subscribe(topic, callback);
		}
	}
	/* eslint-enable class-methods-use-this */

	_loadInitialData() {
		setNewRelicCustomAttribute('isSRP', 'true');
		setNewRelicCustomAttribute('ws-inv-data-refactored', 'true'); // Track usage of refactored class

		const { sortBy = '', start = 0, ...urlParams } = this.parameterMap;

		// Only merge geoData into urlParams when we actually have a geoZip.
		// When SSR resolved no geo (geoZip is null) we leave the URL clean
		// rather than pushing ?geoZip=null&geoRadius=… which would mislead
		// crawlers and shoppers about what the search was bounded by.
		const hasResolvedGeoZip = !!this.geoData?.geoZip;
		const hasGeoParams =
			hasResolvedGeoZip &&
			Object.keys(this.geoData).every((key) =>
				Object.keys(urlParams).includes(key)
			);
		if (hasResolvedGeoZip && !hasGeoParams) {
			this.urlParams = { ...urlParams, ...this.geoData };

			// Populating URL params on page load
			window.history.pushState(
				{
					urlParams: this.urlParams
				},
				'',
				`${window.location.origin}${
					window.location.pathname
				}${this.getQueryString()}`
			);
		} else {
			this.urlParams = urlParams;
		}
		this.srpReady = true;

		this.sortBy = sortBy;
		this.start = start;

		this.originalSearchTerm = null;
		this.forceOriginalSearch = null;
		try {
			this.originalSearchTerm =
				window.localStorage.getItem('originalSearchTerm');
			this.forceOriginalSearch = window.localStorage.getItem(
				'forceOriginalSearch'
			);
		} catch {
			// localStorage inaccessible (Safari private mode / cross-origin iframe)
		}

		const searchInUrlParams = Object.keys(this.urlParams).includes(
			'search'
		);
		const searchValueInUrlParams = searchInUrlParams
			? this.urlParams.search.toString()
			: '';

		if (
			searchInUrlParams &&
			searchValueInUrlParams !== this.originalSearchTerm
		) {
			this._clearForceOriginalSearch();
			this.useOriginalSearchTerm = false;
		} else if (
			searchInUrlParams &&
			searchValueInUrlParams === this.originalSearchTerm
		) {
			this.useOriginalSearchTerm = false;
		} else if (searchInUrlParams) {
			this.useOriginalSearchTerm = true;
		} else {
			this.useOriginalSearchTerm = false;
			this._clearOriginalSearchTerm();
		}

		// check to see if there is search param in the url, it will call request spellcheck endpoint
		if (this._hydrateFromSSR(searchInUrlParams)) {
			// SSR data was valid — inventory + facets populated, pubsub events
			// published, no client fetch needed for the initial page load.
			setNewRelicCustomAttribute('inventoryDataSource', 'server');
		} else if (searchInUrlParams) {
			setNewRelicCustomAttribute(
				'inventoryDataSource',
				'client-fallback'
			);
			this._requestSpellcheck();
		} else {
			setNewRelicCustomAttribute(
				'inventoryDataSource',
				'client-fallback'
			);
			this._requestInventoryAndFacets();
		}
		this._publishSortBy();
	}

	/**
	 * Attempt to hydrate inventory + facets from server-side Redux data.
	 * Returns true if hydration succeeded, false if fallback fetch is needed.
	 *
	 * SSR data is the raw WIS getInventoryAndFacets response dispatched by
	 * store.js → SET_WIS_INVENTORY → Redux state.WIS. We process it the same
	 * way WISutils.requestInventoryAndFacets does on the client.
	 */
	_hydrateFromSSR(searchInUrlParams) {
		const { ssrData } = this;
		// Release reference — SSR data is consumed once at construction time
		this.ssrData = null;

		// Validation: must have inventory array, pageInfo, and facets array to
		// be usable. If facets are missing, hydration "succeeds" with inventory
		// but never publishes ws-inv-data/facets, leaving filter/facet widgets
		// stuck in skeleton with no client-side retry.
		// If the user navigated with a search term, spellcheck must still run
		// client-side, so skip hydration for search pages.
		if (
			searchInUrlParams ||
			!ssrData ||
			!Array.isArray(ssrData.inventory) ||
			!ssrData.pageInfo ||
			!Array.isArray(ssrData.facets)
		) {
			// Default 'unknown' so a future skip branch added without a matching
			// else-if surfaces in NR instead of silently inheriting a stale reason.
			let skipReason = 'unknown';
			if (searchInUrlParams) skipReason = 'searchInUrl';
			else if (!ssrData) skipReason = 'noSsrData';
			else if (!Array.isArray(ssrData.inventory))
				skipReason = 'noInventoryArray';
			else if (!ssrData.pageInfo) skipReason = 'noPageInfo';
			else if (!Array.isArray(ssrData.facets))
				skipReason = 'noFacetsArray';

			setNewRelicCustomAttribute('ssrHydration.result', 'skipped');
			setNewRelicCustomAttribute('ssrHydration.skipReason', skipReason);
			return false;
		}

		// --- NR diagnostics: capture hydration timing for race condition analysis ---
		// These pubsub events fire synchronously during construction (during React
		// render), before consuming widgets have mounted. This is safe NOT because
		// DDC.pubsub retains values (it does not), but because every consumer reads
		// window.DDC.InvData directly at mount and/or polls for it — ws-inv-listing
		// (waitForInvData), ws-inv-facets, and ws-inv-filters (waitForSomething) —
		// so a publish that fires before they subscribe is recovered on first read.
		// ssrHydration.durationMs tracks timing. See issue #336 for the consumer
		// audit and the check-then-subscribe follow-up.
		const hydrateStartTime = Date.now();
		// Track which publishes actually fired. A throwing subscriber propagates
		// back out of publishEvent (ddc-pubsub doesn't isolate subscribers — see
		// DDC-WebPlatform/ddc-pubsub#4), so a throw between the two publishes can
		// leave one un-fired. The catch uses these to decide whether to claim
		// success or let the caller fall back.
		let publishedFacets = false;
		let publishedInventory = false;

		// Wrap the whole post-gate hydration. If a throw happens after BOTH
		// publishes fired, swallow it and return true so Widget.jsx's catch
		// doesn't build a second instance and republish (double-firing
		// subscribers). If a publish was lost, return false so _loadInitialData
		// runs the client fallback that publishes what's missing.
		try {
			// --- Shape facets + inventory via the shared processWISResponse ---
			// (the same normalizer the client-fallback path uses, so the two can't
			// drift — issue #341). The early-return gate above guarantees
			// ssrData.facets/inventory are arrays, so facetResponse.facets is defined.
			const { facetResponse, inventoryResponse } = processWISResponse(
				ssrData,
				{ geoEnabled: !!this.geoData }
			);

			// --- Publish facets (mirrors WISutils Section 1) ---
			this.facets = facetResponse;
			this.appliedFilters = facetResponse.filters || [];
			this.publishEvent('ws-inv-data/facets');
			publishedFacets = true;

			// --- Publish inventory (mirrors WISutils Section 2) ---
			this.inventory = inventoryResponse;
			this.publishEvent('ws-inv-data/inventory');
			publishedInventory = true;

			// SSR hydration fires tracking synchronously during render, so DDC
			// bootstrap may not have set up the dataLayer yet. Record readiness
			// so we can measure how often that happens before deciding whether a
			// deferred retry is worth building — see issue #342.
			setNewRelicCustomAttribute(
				'ssrHydration.dataLayerReady',
				!!window.DDC?.dataLayer?.page?.attributes
			);

			// --- Fire tracking events (mirrors WISutils Section 3) ---
			// Wrapped in try/catch: tracking failures must not prevent SSR
			// hydration from completing. window.DDC.dataLayer or vehicle
			// trackingAttributes may not be available during early hydration.
			try {
				const {
					pageInfo: { trackingData, ...pageInfo }
				} = inventoryResponse;

				setDataLayerTracking(
					this.appliedFilters,
					this.sortBy,
					{ pageInfo, trackingData },
					undefined, // eventType — initial page load, no user event
					{}
				);

				updateDdcDataLayerFromWIS(inventoryResponse, undefined);
			} catch (trackingError) {
				setNewRelicCustomAttribute('ssrHydration.trackingError', true);
				addNewRelicPageAction(
					'WS INV DATA SSR HYDRATION TRACKING ERROR',
					{
						error: trackingError?.message,
						stack: trackingError?.stack?.slice(0, 500)
					}
				);
			}

			// --- NR diagnostics: record hydration outcome ---
			setNewRelicCustomAttribute('ssrHydration.result', 'success');
			setNewRelicCustomAttribute(
				'ssrHydration.durationMs',
				Date.now() - hydrateStartTime
			);
			setNewRelicCustomAttribute(
				'ssrHydration.inventoryCount',
				ssrData.inventory.length
			);

			return true;
		} catch (hydrationError) {
			setNewRelicCustomAttribute('ssrHydration.result', 'error');
			addNewRelicPageAction('WS INV DATA SSR HYDRATION FAILED', {
				error: hydrationError?.message,
				stack: hydrationError?.stack?.slice(0, 500),
				publishedFacets,
				publishedInventory
			});
			// Only claim success when BOTH publishes fired. If we lost one (e.g.
			// a throwing facets subscriber aborted before the inventory publish),
			// return false so _loadInitialData runs the client fallback that
			// publishes what's missing — rather than leaving a consumer stuck.
			return publishedFacets && publishedInventory;
		}
	}
}
